import * as firebase from "firebase";
import * as dimensions from "./dimensions";
import {Dimensions, Point, Rectangle} from "./geometry";
import {globalItem, Item as NativeItem} from "./item";
import {ProjectListItem, User, Item, Result, State, ProjectListProject } from "./models";
import * as store from "./store";
import * as textEditor from "./text_editor";
import {ShowTextEditorData} from "./text_editor";

interface InitData {
  canvasBounds: Rectangle;
  letterSize: Dimensions;
  documentId: string;
}

interface ElmPorts {
  initializeApp: PortToElm<InitData>;
  setUser: PortToElm<User>;
  requestResult: PortFromElm<{items: Item[]}>;
  receiveResult: PortToElm<Result>;
  showTextEditor: PortFromElm<ShowTextEditorData>;
  setText: PortToElm<{id: number | null, value: string, point: Point}>;
  shareData: PortFromElm<Point>;
  saveProject: PortFromElm<{state: State, projectName: string}>;
  deleteProject: PortFromElm<string>;
  requestProjectList: PortFromElm<User | null>;
  receiveProjectList: PortToElm<ProjectListItem[]>;
  authenticate: PortFromElm<void>;
  logOut: PortFromElm<void>;
  showCoordinates: PortToElm<{user: string, point: Point}>;
  updateScrollPosition: PortToElm<Point>;
}

function uuidGenerate(): string {
  return Math.random().toString(16).substr(2, 8) + Math.random().toString(16).substr(2, 8);
}

function isRendered(): boolean {
  return document.querySelector(".project") != null;
}

function initData(documentId: string): InitData {
  return {...dimensions.appDimensions(), documentId: documentId};
}

function buildGuestUser(): User {
  const userJson = window.localStorage.getItem("textik_user");
  let user;
  if (userJson != null) {
    user = JSON.parse(userJson);
  }
  if (user == null) {
    user = {uid: "guest_" + uuidGenerate(), isGuest: true, email: ""};
    window.localStorage.setItem("textik_user", JSON.stringify(user));
  }
  return user;
}

function buildUser(userPayload: firebase.UserInfo): User {
  return {
    email: userPayload.email || "",
    isGuest: !userPayload.email,
    uid: userPayload.uid
  };
}

let connections: string[] = [];

function initialize(documentId: string, app: ElmComponent<ElmPorts>, initialUser: User): void {
  let user = initialUser;
  if (isRendered()) {
    window.addEventListener("keydown", (e: KeyboardEvent) => {
      // left, right, top, bottom
      if (e.which >= 37 && e.which <= 40 && (e.target as HTMLElement).tagName !== "INPUT") {
        e.preventDefault();
      }
    });
    const initialData = initData(documentId);
    const projectList = !user.isGuest ? store.getProjectList(user) : Promise.resolve([]);
    app.ports.initializeApp.send(initialData);
    app.ports.setUser.send(user);
    app.ports.requestResult.subscribe((payload) => {
      app.ports.receiveResult.send(globalItem.buildResult(payload.items));
    });
    app.ports.showTextEditor.subscribe((payload) => {
      textEditor.showTextEditor(payload, (newValue) => {
        app.ports.setText.send({
          id: payload.id,
          value: newValue,
          point: {x: payload.bounds.start.x, y: payload.bounds.start.y}
        });
      });
    });
    app.ports.shareData.subscribe((payload) => {
      if (connections.length > 1) {
        shareCoordinates(documentId, user.uid, payload);
      }
    });
    app.ports.saveProject.subscribe((payload) => {
      saveProject(user, documentId, payload.state, payload.projectName);
    });
    app.ports.deleteProject.subscribe((projectId) => {
      if (confirm("Are you sure?")) {
        store.deleteProject(user, projectId).then((_) => {
          return loadProjectList(store.getProjectList(user));
        }).then((list) => {
          return app.ports.receiveProjectList.send(list);
        });
      }
    });
    app.ports.authenticate.subscribe(() => {
      store.authenticate().then((result) => {
        user = buildUser(result.user);
        store.updateUser(user).then(() => {
          document.location.reload();
        });
      });
    });
    app.ports.requestProjectList.subscribe((_) => {
      loadProjectList(projectList).then((list) => {
        app.ports.receiveProjectList.send(list);
      });
    });
    app.ports.logOut.subscribe(() => {
      store.logOut().then(() => {
        user = buildGuestUser();
        app.ports.setUser.send(user);
      });
    });

    store.registerConnection(documentId, user.uid);

    store.listenToConnections(documentId, (snapshot) => {
      const value = snapshot.val();
      if (value != null) {
        connections = objectValues(value as {[k: string]: string});
      }
    });
    store.listenToCoordinates(documentId, (snapshot) => {
      const payload = snapshot.val();
      if (payload != null) {
        Object.keys(payload).forEach((userName) => {
          const point = payload[userName];
          if (userName !== user.email) {
            app.ports.showCoordinates.send({user: userName, point: point});
          }
        });
      }
    });

    const projectNode: Element | null = document.querySelector(".project");
    if (projectNode != null) {
      projectNode.addEventListener("scroll", () => {
        app.ports.updateScrollPosition.send(dimensions.scrollPosition(projectNode));
      });
    } else {
      throw "Missing .project while initializing on scroll event handler";
    }
  } else {
    setTimeout(() => initialize(documentId, app, user), 5);
  }
}

const saveProject = throttle(store.saveProject);
const shareCoordinates = throttle(store.shareCoordinates);

function throttle(fn: Function, threshold: number = 250, scope?: any): Function {
  let last;
  let deferTimer;
  /* tslint:disable-next-line */
  return function () {
    const context = scope || this;

    const now = +new Date();
    const args = arguments;
    if (last && now < last + threshold) {
      // hold on to it
      clearTimeout(deferTimer);
      deferTimer = setTimeout(() => {
        last = now;
        fn.apply(context, args);
      }, threshold + last - now);
    } else {
      last = now;
      fn.apply(context, args);
    }
  };
}

function objectValues<T>(value: {[k: string]: T}): T[] {
  return Object.keys(value).map((key) => {
    return value[key];
  });
}

function loadProjectList(projectList: firebase.Promise<ProjectListProject[]>): firebase.Promise<ProjectListItem[]> {
  return projectList.then((projects) => {
    const list = projects.map((project) => {
      const textikItem = new NativeItem();
      return {
        name: project.name,
        projectId: project.id,
        result: textikItem.buildResult(project.state.completed)
      };
    });
    return list;
  });
}

export function main(): void {
  let projectPromise;
  let id = location.hash.replace("#", "");
  if (id === "") {
    id = uuidGenerate();
    location.hash = id;
    projectPromise = Promise.resolve({});
  } else {
    projectPromise = new Promise((resolve, _) => {
      store.getProject(id).then((project) => {
        resolve(project || {});
      });
    });
  }

  let userPromise = new Promise((resolve, _) => {
    const unsubscribe = store.auth.onAuthStateChanged((user: firebase.UserInfo) => {
      unsubscribe();
      if (user) {
        resolve(buildUser(user));
      } else {
        resolve(buildGuestUser());
      }
    });
  });

  Promise.all([userPromise, projectPromise]).then(([user, project]) => {
    // const screen = project != null ? "EditorScreen" : "NotFoundScreen";
    const screen = "EditorScreen";
    project = project != null && project.completed != null ? project : null;
    const appNode = document.querySelector("#app");
    if (appNode != null) {
      const app = Elm["Main"].embed(appNode, {
        user: user,
        state: project,
        screen: screen,
        projectName: "My Project 1"
      }) as ElmComponent<ElmPorts>;

      setTimeout(() => initialize(id, app, user as User), 5);
    }
  });
}



// WEBPACK FOOTER //
// ./assets/js/app.ts


// WEBPACK FOOTER //
// ./assets/js/app.ts